import type {
  LauncherSearchItem,
  LauncherResultSections,
  LauncherContext,
  RankingWeights,
  LauncherLimits,
} from './types';
import { DEFAULT_RANKING_WEIGHTS, DEFAULT_LAUNCHER_LIMITS } from './types';
import { fuzzyMatch, fuzzyMatchArray } from './fuzzySearch';

type ScoredItem = {
  item: LauncherSearchItem;
  score: number;
};

function scoreItem(
  item: LauncherSearchItem,
  query: string,
  context: LauncherContext,
  weights: RankingWeights,
  frequentAccessThreshold: number = DEFAULT_LAUNCHER_LIMITS.frequentAccessThreshold,
): number {
  const q = query.toLowerCase();
  let score = 0;

  const titleLower = item.title.toLowerCase();
  if (titleLower === q) {
    score = weights.exactTitleMatch;
  } else if (titleLower.startsWith(q)) {
    score = weights.titleStartsWith;
  } else if (titleLower.includes(q)) {
    score = weights.titleContains;
  }

  if (item.aliases.length > 0) {
    const aliasLower = item.aliases.map(a => a.toLowerCase());
    if (aliasLower.some(a => a === q)) {
      score = Math.max(score, weights.aliasExactMatch);
    } else {
      const aliasScore = fuzzyMatchArray(item.aliases, query);
      if (aliasScore > 0) {
        score = Math.max(score, weights.aliasFuzzyMatch * (aliasScore / 100));
      }
    }
  }

  if (item.taskPhrases.length > 0) {
    const phraseScore = fuzzyMatchArray(item.taskPhrases, query);
    if (phraseScore > 0) {
      score = Math.max(score, weights.taskPhraseMatch * (phraseScore / 100));
    }
  }

  if (item.keywords.length > 0) {
    const kwLower = item.keywords.map(k => k.toLowerCase());
    if (kwLower.some(k => k === q || k.startsWith(q))) {
      score = Math.max(score, weights.keywordMatch);
    } else {
      const kwScore = fuzzyMatchArray(item.keywords, query);
      if (kwScore > 0) {
        score = Math.max(score, weights.keywordMatch * (kwScore / 100));
      }
    }
  }

  if (score === 0 && item.description) {
    const descScore = fuzzyMatch(item.description, query);
    if (descScore > 0) {
      score = weights.descriptionMatch * (descScore / 100);
    }
  }

  // Also try matching against ID (plugin id often used as search term)
  if (score === 0) {
    const idScore = fuzzyMatch(item.id, query);
    if (idScore > 0) {
      score = weights.descriptionMatch * (idScore / 100);
    }
  }

  if (score === 0) return 0;

  // Contextual boosts (additive)
  if (item.pinned) score += weights.pinnedBoost;
  if (item.lastAccessedAt) score += weights.recentBoost;
  if (item.accessCount && item.accessCount > frequentAccessThreshold) score += weights.frequentBoost;
  if (context.currentGroup && item.group === context.currentGroup) {
    score += weights.sameGroupBoost;
  }
  if (item.disabled) score += weights.disabledPenalty;

  return score;
}

export function rankLauncherResults(
  items: LauncherSearchItem[],
  query: string,
  context: LauncherContext = {},
  weights: RankingWeights = DEFAULT_RANKING_WEIGHTS,
  limits: LauncherLimits = DEFAULT_LAUNCHER_LIMITS,
): LauncherResultSections {
  const trimmedQuery = query.trim();

  if (!trimmedQuery) {
    return buildEmptyQuerySections(items, context, limits);
  }

  const scored: ScoredItem[] = [];
  for (const item of items) {
    const score = scoreItem(item, trimmedQuery, context, weights, limits.frequentAccessThreshold);
    if (score > 0) {
      scored.push({ item, score });
    }
  }

  scored.sort((a, b) => b.score - a.score);

  const bestMatch = scored.slice(0, limits.maxBestMatch).map(s => s.item);
  const otherMatches = scored.slice(limits.maxBestMatch, limits.maxBestMatch + limits.maxOtherMatch).map(s => s.item);
  const actions = scored.filter(s => s.item.type === 'action').slice(0, limits.maxActions).map(s => s.item);

  return {
    pinned: [],
    recent: [],
    bestMatch,
    otherMatches,
    actions,
  };
}

function buildEmptyQuerySections(
  items: LauncherSearchItem[],
  context: LauncherContext,
  limits: LauncherLimits,
): LauncherResultSections {
  const pinned = items
    .filter(i => i.pinned && !i.disabled)
    .slice(0, limits.maxPinned);

  const recent = items
    .filter(i => i.lastAccessedAt && !i.pinned)
    .sort((a, b) => (b.lastAccessedAt ?? 0) - (a.lastAccessedAt ?? 0))
    .slice(0, limits.maxRecent);

  const recentIds = new Set(recent.map(i => i.id));
  const pinnedIds = new Set(pinned.map(i => i.id));

  const recommended = context.currentGroup
    ? items
        .filter(i =>
          i.group === context.currentGroup &&
          !recentIds.has(i.id) &&
          !pinnedIds.has(i.id) &&
          !i.disabled
        )
        .slice(0, limits.maxRecommended)
    : [];

  return {
    pinned,
    recent,
    bestMatch: recommended,
    otherMatches: [],
    actions: [],
  };
}

export function getTotalResultCount(sections: LauncherResultSections): number {
  return (
    sections.pinned.length +
    sections.recent.length +
    sections.bestMatch.length +
    sections.otherMatches.length +
    sections.actions.length
  );
}

export function getItemAtIndex(
  sections: LauncherResultSections,
  index: number,
): LauncherSearchItem | null {
  const all = [
    ...sections.pinned,
    ...sections.recent,
    ...sections.bestMatch,
    ...sections.otherMatches,
    ...sections.actions,
  ];
  return all[index] ?? null;
}
